import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ArrowDown, ArrowUp } from 'lucide-react';
import {
  type DeviceLldpNeighbor,
  type DeviceLldpSource,
  useDeviceLldp,
} from '@/hooks/useDeviceLldp';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { EmptyState } from '@/components/ui/empty-state';
import { cn } from '@/lib/utils';

interface DeviceLldpTabProps {
  deviceId: string | undefined;
}

type SortKey =
  | 'localPort'
  | 'neighborName'
  | 'remotePort'
  | 'neighborChassisId'
  | 'neighborManagementIp'
  | 'lastSeenAt';

interface SortState {
  key: SortKey;
  dir: 'asc' | 'desc';
}

function compareNeighbors(
  a: DeviceLldpNeighbor,
  b: DeviceLldpNeighbor,
  sort: SortState,
): number {
  const av = a[sort.key];
  const bv = b[sort.key];
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  const cmp = av.localeCompare(bv, undefined, { numeric: true });
  return sort.dir === 'asc' ? cmp : -cmp;
}

function sourceLabel(source: DeviceLldpSource): string {
  switch (source) {
    case 'snmp':
      return 'SNMP (LLDP-MIB)';
    case 'cli_lldpctl':
      return 'CLI (lldpctl)';
    default:
      return source;
  }
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffMs = Date.now() - then;
  if (diffMs < 0) return 'in the future';
  const secs = Math.floor(diffMs / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${String(mins)} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `${String(hours)} hr ago`;
  const days = Math.floor(hours / 24);
  return `${String(days)} days ago`;
}

function SortHeader({
  label,
  sortKey,
  sort,
  toggleSort,
}: {
  label: string;
  sortKey: SortKey;
  sort: SortState;
  toggleSort: (key: SortKey) => void;
}) {
  const active = sort.key === sortKey;
  return (
    <th
      className="text-left py-2 px-3 text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer select-none hover:text-foreground"
      onClick={() => { toggleSort(sortKey); }}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          sort.dir === 'asc' ? (
            <ArrowUp className="w-3 h-3" />
          ) : (
            <ArrowDown className="w-3 h-3" />
          )
        ) : null}
      </span>
    </th>
  );
}

export default function DeviceLldpTab({ deviceId }: DeviceLldpTabProps) {
  const [sort, setSort] = useState<SortState>({ key: 'localPort', dir: 'asc' });
  const { data, isLoading, isError, isFetching } = useDeviceLldp(deviceId);

  const sortedNeighbors = useMemo(() => {
    if (!data) return [];
    return [...data.neighbors].sort((a, b) => compareNeighbors(a, b, sort));
  }, [data, sort]);

  const toggleSort = (key: SortKey) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' },
    );
  };

  if (isLoading) {
    return <Skeleton className="h-64 w-full" />;
  }

  if (isError || !data) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Failed to load LLDP neighbors.</AlertDescription>
      </Alert>
    );
  }

  const { status } = data;

  return (
    <section className="bg-card border border-border rounded-lg p-5">
      {/* Status header */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4">
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Source:</span>
          <Badge variant="secondary" className="font-normal">
            {sourceLabel(status.source)}
          </Badge>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Status:</span>
          <Badge
            className={cn(
              'border-0 font-normal',
              status.lldpEnabled
                ? 'bg-sev-4/15 text-sev-4'
                : 'bg-muted text-muted-foreground',
            )}
            title="True when at least one neighbor has been observed on this device."
          >
            {status.lldpEnabled ? 'Receiving' : 'No data'}
          </Badge>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Last update:</span>
          <span title={status.lastNeighborSeenAt ?? 'no observations yet'}>
            {status.lastNeighborSeenAt ? relativeTime(status.lastNeighborSeenAt) : '—'}
          </span>
        </div>
        {isFetching ? (
          <span className="ml-auto text-xs text-muted-foreground">Refreshing…</span>
        ) : null}
      </div>

      {sortedNeighbors.length === 0 ? (
        <EmptyState
          variant="no-data"
          title="No LLDP neighbors yet"
          description={
            status.source === 'snmp' ? (
              <>
                If the device runs <code className="font-mono">lldpd</code> but its SNMP
                agent doesn't expose LLDP-MIB (e.g. Ubiquiti EdgeOS), the vendor profile
                should opt in to <code className="font-mono">cli_lldpctl</code> polling.
              </>
            ) : null
          }
          size="inline"
        />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-border-subtle">
              <tr>
                <SortHeader label="Local port" sortKey="localPort" sort={sort} toggleSort={toggleSort} />
                <SortHeader label="Neighbor" sortKey="neighborName" sort={sort} toggleSort={toggleSort} />
                <SortHeader label="Remote port" sortKey="remotePort" sort={sort} toggleSort={toggleSort} />
                <SortHeader label="Chassis ID" sortKey="neighborChassisId" sort={sort} toggleSort={toggleSort} />
                <SortHeader label="Management IP" sortKey="neighborManagementIp" sort={sort} toggleSort={toggleSort} />
                <SortHeader label="Last seen" sortKey="lastSeenAt" sort={sort} toggleSort={toggleSort} />
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle">
              {sortedNeighbors.map((n) => (
                <tr key={n.connectionId} className="hover:bg-bg-subtle">
                  <td className="py-2 px-3">
                    <code className="font-mono text-xs">
                      {n.localPort ?? <span className="text-muted-foreground">—</span>}
                    </code>
                    {n.localPortDescription ? (
                      <div className="text-xs text-muted-foreground" title={n.localPortDescription}>
                        {n.localPortDescription}
                      </div>
                    ) : null}
                  </td>
                  <td className="py-2 px-3">
                    <Link to={`/devices/${n.neighborDeviceId}`} className="hover:underline">
                      {n.neighborName ?? (
                        <span className="text-muted-foreground italic">unnamed neighbor</span>
                      )}
                    </Link>
                    {n.neighborSystemDescription ? (
                      <div
                        className="text-xs text-muted-foreground truncate max-w-[24em]"
                        title={n.neighborSystemDescription}
                      >
                        {n.neighborSystemDescription}
                      </div>
                    ) : null}
                  </td>
                  <td className="py-2 px-3">
                    <code className="font-mono text-xs">
                      {n.remotePort ?? <span className="text-muted-foreground">—</span>}
                    </code>
                    {n.remotePortDescription && n.remotePortDescription !== n.remotePort ? (
                      <div className="text-xs text-muted-foreground">
                        {n.remotePortDescription}
                      </div>
                    ) : null}
                  </td>
                  <td className="py-2 px-3">
                    <code className="font-mono text-xs">
                      {n.neighborChassisId ?? <span className="text-muted-foreground">—</span>}
                    </code>
                  </td>
                  <td className="py-2 px-3">
                    {n.neighborManagementIp ? (
                      <code className="font-mono text-xs">{n.neighborManagementIp}</code>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-xs text-muted-foreground" title={n.lastSeenAt}>
                    {relativeTime(n.lastSeenAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
